import { useState, useEffect, useMemo } from "react";
import { Tag, Calendar, ShoppingBasket, Filter, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import SEO from "@/components/SEO";
import NewsletterPopup from "@/components/NewsletterPopup";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// All available categories for filtering
const allCategories = [
  "Blommor",
  "Bröd & Bakverk",
  "Bröd - Eget",
  "Chark",
  "Djupfryst",
  "Djur",
  "Drycker",
  "Fisk",
  "Frukt & Grönt",
  "Grov-kem",
  "Konfektyr",
  "Kroppsvård",
  "Kött",
  "Kött - Eget i disk",
  "Läkemedel",
  "Mejeri",
  "Ost",
  "Snacks",
  "Special",
  "Specerier",
  "Tobak",
  "Vegetariskt",
];

// Convert image URLs - handles both uploaded files and external URLs
function getImageUrl(url) {
  if (!url) return null;
  
  // If it starts with /api/images (new MongoDB storage), prepend the base URL
  if (url.startsWith('/api/images')) {
    return `${BASE_URL}${url}`;
  }
  
  // If it starts with /uploads or /api/uploads (legacy), prepend the base URL
  if (url.startsWith('/uploads')) {
    return `${BASE_URL}/api${url}`;
  }
  if (url.startsWith('/api/uploads')) {
    return `${BASE_URL}${url}`;
  }
  
  // Already a direct link or other URL
  return url;
}

export default function OffersPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({
    campaign_week: "v.10",
    campaign_date: "03/03 - 09/03",
    campaign_year: 2026
  });

  // Get unique categories that exist in offers
  const availableCategories = useMemo(() => {
    const cats = [...new Set(offers.map(offer => offer.category).filter(Boolean))];
    return allCategories.filter(cat => cats.includes(cat));
  }, [offers]);

  // Filter offers by selected category
  const filteredOffers = useMemo(() => {
    if (selectedCategory === "all") return offers;
    return offers.filter(offer => offer.category === selectedCategory);
  }, [offers, selectedCategory]);

  function getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [offersRes, settingsRes] = await Promise.all([
          axios.get(`${API}/offers/current`),
          axios.get(`${API}/settings`)
        ]);
        setOffers(offersRes.data);
        setSiteSettings(settingsRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="page-transition">
      <SEO 
        title="Veckans Erbjudanden - Bästa Priserna"
        description="Se veckans bästa erbjudanden på Mathallen Malmö. Färska produkter, kött, grönsaker och mycket mer till kampanjpriser. Nya erbjudanden varje vecka!"
        keywords="veckans erbjudanden Malmö, kampanjer stormarknad, REA mat Malmö, billiga matvaror, erbjudanden kött, erbjudanden grönsaker, Mathallen kampanj"
        url="/erbjudanden"
      />
      
      {/* Newsletter Popup - Only on this page */}
      <NewsletterPopup />
      
      {/* Hero - Split Layout */}
      <section className="relative bg-stone-900 overflow-hidden">
        <div className="grid md:grid-cols-2 min-h-[400px] md:min-h-[450px] lg:min-h-[500px]">
          {/* Left - Content */}
          <div className="flex flex-col justify-center px-6 md:px-8 lg:pl-[max(2rem,calc((100vw-1280px)/2+2rem))] lg:pr-16 py-12 md:py-16 order-2 md:order-1">
            <span className="inline-flex items-center gap-2 text-red-400 font-medium text-sm mb-4 bg-red-500/10 px-4 py-2 rounded-full w-fit">
              <Tag className="w-4 h-4" />
              Veckans bästa priser
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-4 md:mb-6">
              Veckans <span className="text-red-500">erbjudanden</span>
            </h1>
            <p className="text-base md:text-lg text-stone-300 leading-relaxed max-w-lg mb-6">
              Upptäck veckans bästa priser på utvalda favoriter – passa på att spara mer.
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl px-5 py-3 w-fit">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-red-400" />
                <span className="font-semibold text-white">Gäller {siteSettings.campaign_year} {siteSettings.campaign_week}</span>
              </div>
              <p className="text-stone-300 text-sm mt-1 ml-7">{siteSettings.campaign_date}</p>
            </div>
          </div>
          
          {/* Right - Video (full bleed) */}
          <div className="relative h-56 sm:h-64 md:h-auto order-1 md:order-2 bg-stone-800">
            <video
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
            >
              <source src="https://customer-assets.emergentagent.com/job_stormarknad-malmo/artifacts/xpnxhyg9_video-1.mov" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-stone-900/60 via-transparent to-transparent md:from-stone-900 md:via-transparent md:to-transparent" />
          </div>
        </div>
      </section>

      {/* Offers Grid with Filter */}
      <section className="py-12 md:py-16 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          
          {/* Filter Section - Top on all screens */}
          {!loading && availableCategories.length > 0 && (
            <div className="mb-8">
              {/* Mobile Filter - Dropdown */}
              <div className="md:hidden">
                <button
                  onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                  className="w-full flex items-center justify-between bg-white px-4 py-3 rounded-xl shadow-sm border border-stone-200"
                  data-testid="mobile-filter-toggle"
                >
                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-stone-600" />
                    <span className="font-medium text-stone-800">
                      {selectedCategory === "all" ? "Filtrera kategori" : selectedCategory}
                    </span>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-stone-500 transition-transform ${mobileFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Mobile Filter Dropdown */}
                {mobileFilterOpen && (
                  <div className="mt-2 bg-white rounded-xl shadow-lg border border-stone-200 overflow-hidden">
                    <button
                      onClick={() => {
                        setSelectedCategory("all");
                        setMobileFilterOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors ${
                        selectedCategory === "all" 
                          ? "bg-red-50 text-red-700" 
                          : "text-stone-700 hover:bg-stone-50"
                      }`}
                      data-testid="mobile-filter-all"
                    >
                      Visa alla ({offers.length})
                    </button>
                    {availableCategories.map(category => {
                      const count = offers.filter(o => o.category === category).length;
                      return (
                        <button
                          key={category}
                          onClick={() => {
                            setSelectedCategory(category);
                            setMobileFilterOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 text-sm font-medium transition-colors border-t border-stone-100 ${
                            selectedCategory === category 
                              ? "bg-red-50 text-red-700" 
                              : "text-stone-700 hover:bg-stone-50"
                          }`}
                          data-testid={`mobile-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {category} ({count})
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Desktop Filter - Horizontal Pills */}
              <div className="hidden md:block">
                <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Filter className="w-5 h-5 text-red-600" />
                    <span className="font-semibold text-stone-900">Filtrera efter kategori</span>
                    {selectedCategory !== "all" && (
                      <span className="text-stone-500 text-sm">({filteredOffers.length} produkter)</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedCategory("all")}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedCategory === "all" 
                          ? "bg-red-600 text-white shadow-sm" 
                          : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                      }`}
                      data-testid="desktop-filter-all"
                    >
                      Visa alla ({offers.length})
                    </button>
                    {availableCategories.map(category => {
                      const count = offers.filter(o => o.category === category).length;
                      return (
                        <button
                          key={category}
                          onClick={() => setSelectedCategory(category)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                            selectedCategory === category 
                              ? "bg-red-600 text-white shadow-sm" 
                              : "bg-stone-100 text-stone-700 hover:bg-stone-200"
                          }`}
                          data-testid={`desktop-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {category} ({count})
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Active Filter Indicator */}
          {selectedCategory !== "all" && (
            <div className="mb-6 flex items-center gap-2">
              <span className="text-stone-600 text-sm">Filtrerat på:</span>
              <span className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-3 py-1.5 rounded-full text-sm font-medium">
                {selectedCategory}
                <button 
                  onClick={() => setSelectedCategory("all")}
                  className="hover:bg-red-200 rounded-full p-0.5 transition-colors"
                  data-testid="clear-filter-button"
                >
                  <X className="w-4 h-4" />
                </button>
              </span>
            </div>
          )}

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 animate-pulse">
                  <div className="w-full aspect-square bg-stone-200 rounded-xl mb-4" />
                  <div className="h-4 bg-stone-200 rounded w-3/4 mb-2" />
                  <div className="h-6 bg-stone-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : filteredOffers.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {filteredOffers.map((offer, index) => (
                <div
                  key={offer.id}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 border border-stone-100"
                  data-testid={`offer-item-${index}`}
                >
                  {/* Product Image - Full width, fills container */}
                  <div className="aspect-square overflow-hidden relative">
                    {offer.image_url ? (
                      <img
                        src={getImageUrl(offer.image_url)}
                        alt={offer.product_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-stone-100 flex items-center justify-center">
                        <ShoppingBasket className="w-16 h-16 text-stone-300" />
                      </div>
                    )}
                    
                    {/* Bästa Pris Ribbon - Top Left Corner */}
                    {offer.is_best_price && (
                      <div className="absolute top-0 left-0 overflow-hidden w-24 h-24 md:w-28 md:h-28">
                        <div className="absolute transform -rotate-45 bg-red-600 text-white text-center font-bold py-1 left-[-34px] top-[20px] w-[140px] md:left-[-30px] md:top-[24px] md:w-[150px] shadow-md text-xs md:text-sm">
                          Bästa Pris
                        </div>
                      </div>
                    )}
                    
                    {/* Multi-buy Badge - Only show if unit is also set */}
                    {offer.multi_buy && offer.unit && (
                      <div className="absolute top-3 right-3 md:top-4 md:right-4">
                        <div className="bg-red-600 rounded-full px-4 py-2 md:px-5 md:py-2.5 shadow-lg">
                          <span className="text-white font-black text-lg md:text-xl tracking-tight">
                            {offer.multi_buy} För
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Price Tag - Below image, full width */}
                  <div className="bg-yellow-400 px-4 py-3 md:py-4 text-center ticket-edge">
                    {/* Show "X För" on the left of price if multi_buy exists but no unit */}
                    {offer.multi_buy && !offer.unit ? (
                      <>
                        <span className="text-red-700 font-bold text-xs md:text-sm block leading-tight">Kampanj</span>
                        <div className="flex items-center justify-center gap-2">
                          <span className="bg-red-600 text-white font-bold text-sm md:text-base px-2 py-0.5 rounded">
                            {offer.multi_buy} För
                          </span>
                          <div className="flex items-baseline">
                            <span className="text-red-700 font-black text-3xl md:text-4xl leading-none">
                              {Math.floor(offer.offer_price)}
                            </span>
                            {offer.offer_price % 1 !== 0 ? (
                              <>
                                <span className="text-red-700 font-black text-3xl md:text-4xl leading-none">.</span>
                                <span className="text-red-700 font-bold text-xl md:text-2xl relative -top-2 -ml-0.5">
                                  {Math.round((offer.offer_price % 1) * 100).toString().padStart(2, '0')}
                                </span>
                              </>
                            ) : (
                              <span className="text-red-700 font-black text-3xl md:text-4xl leading-none">:-</span>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <span className="text-red-700 font-bold text-xs md:text-sm block leading-tight">Kampanj</span>
                        <div className="flex items-baseline justify-center">
                          <span className="text-red-700 font-black text-3xl md:text-4xl leading-none">
                            {Math.floor(offer.offer_price)}
                          </span>
                          {offer.offer_price % 1 !== 0 ? (
                            <>
                              <span className="text-red-700 font-black text-3xl md:text-4xl leading-none">.</span>
                              <span className="text-red-700 font-bold text-xl md:text-2xl relative -top-2 -ml-0.5">
                                {Math.round((offer.offer_price % 1) * 100).toString().padStart(2, '0')}
                              </span>
                            </>
                          ) : (
                            <span className="text-red-700 font-black text-3xl md:text-4xl leading-none">:-</span>
                          )}
                          {offer.unit && (
                            <span className="text-red-700 font-semibold text-lg md:text-xl ml-0.5">/{offer.unit}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  
                  {/* Product Info */}
                  <div className="p-4 text-center">
                    <h3 className="font-bold text-stone-900 text-sm md:text-base leading-tight mb-1">
                      {offer.product_name}
                    </h3>
                    <p className="text-stone-500 text-xs md:text-sm uppercase tracking-wide mb-3">
                      {offer.category}
                    </p>
                    {offer.original_price && (
                      <div className="bg-stone-100 rounded-lg py-2 px-3">
                        <span className="text-stone-600 text-xs md:text-sm font-medium">Ord pris </span>
                        <span className="text-stone-700 text-sm md:text-base font-bold whitespace-nowrap">
                          {Math.floor(offer.original_price)}
                          {offer.original_price % 1 !== 0 ? (
                            <>
                              <span>.</span>
                              <span className="text-xs relative -top-1">
                                {Math.round((offer.original_price % 1) * 100).toString().padStart(2, '0')}
                              </span>
                            </>
                          ) : (
                            <span>:-</span>
                          )}
                          {offer.unit ? `/${offer.unit}` : ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-white rounded-2xl">
              <ShoppingBasket className="w-20 h-20 text-stone-200 mx-auto mb-6" />
              <h3 className="text-xl font-semibold text-stone-900 mb-2">
                Inga erbjudanden i denna kategori
              </h3>
              <p className="text-stone-500 mb-6">
                Det finns inga aktiva erbjudanden i "{selectedCategory}" just nu.
              </p>
              <Button
                onClick={() => setSelectedCategory("all")}
                variant="outline"
                className="rounded-full"
                data-testid="reset-filter-button"
              >
                Visa alla kategorier
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-12 bg-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-2xl font-bold mb-2">
                Nya erbjudanden varje vecka
              </h2>
              <p className="text-stone-400">
                Besök oss ofta för att inte missa de bästa kampanjerna!
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center px-6 py-4 bg-stone-800 rounded-xl">
                <p className="text-3xl font-bold text-red-500">45.000+</p>
                <p className="text-stone-400 text-sm">Produkter i sortimentet</p>
              </div>
              <div className="text-center px-6 py-4 bg-stone-800 rounded-xl">
                <p className="text-3xl font-bold text-red-500">5+</p>
                <p className="text-stone-400 text-sm">År av erfarenhet</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
